import { useEffect, useState, useCallback } from "react";
import { Boxes, AlertTriangle, PackageX, DollarSign, Layers, Download, Plus, Minus, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const fmt = (c) => `$${((c || 0) / 100).toFixed(2)}`;

function timeAgo(iso) {
  if (!iso) return "—";
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

function StatCard({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    neutral: "bg-white text-slate-900",
    blue: "bg-blue-50 text-blue-900 border-blue-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    red: "bg-red-50 text-red-900 border-red-200",
    green: "bg-emerald-50 text-emerald-900 border-emerald-200",
  };
  return (
    <div className={`rounded-md border border-slate-200 p-5 ${tones[tone] || tones.neutral}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-widest opacity-70">{label}</span>
        <Icon className="h-4 w-4 opacity-60" />
      </div>
      <div className="mt-2 font-display text-3xl font-bold">{value}</div>
    </div>
  );
}

export default function InventoryAdmin() {
  const [report, setReport] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [editing, setEditing] = useState(null); // { product, delta, reason }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, a] = await Promise.all([api.get("/inventory/report"), api.get("/inventory/adjustments?limit=20")]);
      setReport(r.data);
      setAdjustments(a.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load inventory"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitAdjust = async () => {
    if (!editing) return;
    const delta = parseInt(editing.delta, 10);
    if (!delta || isNaN(delta)) { toast.error("Enter a non-zero number"); return; }
    setBusy(true);
    try {
      await api.post(`/inventory/adjust/${editing.product.id}`, { delta, reason: editing.reason });
      toast.success(`${editing.product.name} updated`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Adjustment failed"); }
    finally { setBusy(false); }
  };

  const downloadCsv = async () => {
    // Use cookie-auth: opening in a new tab carries the session cookie automatically.
    const url = `${API}/inventory/export.csv`;
    window.open(url, "_blank", "noopener");
  };

  if (!report) return <div className="text-slate-500">Loading…</div>;

  const t = report.totals;

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 inline-flex items-center gap-2"><Boxes className="h-6 w-6 text-cyan-700" /> Inventory</h1>
          <p className="mt-1 text-sm text-slate-500">Generated {timeAgo(report.generated_at)} · auto-recompute on every load</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="inventory-print-button">Print report</Button>
          <Button onClick={downloadCsv} data-testid="inventory-export-csv-button"><Download className="h-4 w-4" /> Export CSV</Button>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Products" value={t.products} icon={Layers} tone="neutral" testid="inv-stat-products" />
        <StatCard label="Total units" value={t.units} icon={Boxes} tone="blue" testid="inv-stat-units" />
        <StatCard label="Inventory value" value={fmt(t.value_cents)} icon={DollarSign} tone="green" testid="inv-stat-value" />
        <StatCard label="Low stock" value={t.low_stock} icon={AlertTriangle} tone="amber" testid="inv-stat-low" />
        <StatCard label="Out of stock" value={t.out_of_stock} icon={PackageX} tone="red" testid="inv-stat-out" />
      </div>

      <section className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="dash-surface lg:col-span-2 p-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">By category</div>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                <th className="py-2">Category</th>
                <th className="py-2 text-right">Products</th>
                <th className="py-2 text-right">Units</th>
                <th className="py-2 text-right">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {report.by_category.map((c) => (
                <tr key={c.category} data-testid={`inv-cat-${c.category}`}>
                  <td className="py-2 capitalize">{c.category}</td>
                  <td className="py-2 text-right">{c.products}</td>
                  <td className="py-2 text-right">{c.units}</td>
                  <td className="py-2 text-right">{fmt(c.value_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="dash-surface p-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" /> Low stock & out of stock
          </div>
          {report.out_of_stock_items.length === 0 && report.low_stock_items.length === 0 ? (
            <p className="mt-3 text-sm text-slate-500">All clear — every product has healthy stock.</p>
          ) : (
            <ul className="mt-3 space-y-2 text-sm">
              {report.out_of_stock_items.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 rounded-md bg-red-50 px-3 py-2 text-red-900">
                  <span className="truncate">{p.name}</span>
                  <span className="text-xs font-semibold uppercase">Out</span>
                </li>
              ))}
              {report.low_stock_items.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 rounded-md bg-amber-50 px-3 py-2 text-amber-900">
                  <span className="truncate">{p.name}</span>
                  <span className="text-xs">{p.stock} left</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="mt-6 dash-surface overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3 text-right">Price</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Line value</th>
              <th className="px-4 py-3">Adjust</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {report.products.map((p) => {
              const stock = p.stock || 0;
              const threshold = p.low_stock_threshold ?? 5;
              const tone = stock <= 0 ? "text-red-600" : stock <= threshold ? "text-amber-600" : "text-emerald-700";
              return (
                <tr key={p.id} data-testid={`inv-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 truncate">{p.name}</div>
                  </td>
                  <td className="px-4 py-3 text-sm capitalize text-slate-600">{p.category}</td>
                  <td className="px-4 py-3 text-right text-sm">{fmt(p.price_cents)}</td>
                  <td className={`px-4 py-3 text-right font-display text-lg font-bold ${tone}`}>{stock}</td>
                  <td className="px-4 py-3 text-right text-sm text-slate-700">{fmt(stock * p.price_cents)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => setEditing({ product: p, delta: "-1", reason: "" })} data-testid={`inv-adjust-down-${p.id}`}><Minus className="h-3 w-3" /></Button>
                      <Button variant="outline" size="sm" onClick={() => setEditing({ product: p, delta: "1", reason: "" })} data-testid={`inv-adjust-up-${p.id}`}><Plus className="h-3 w-3" /></Button>
                      <Button variant="outline" size="sm" onClick={() => setEditing({ product: p, delta: "", reason: "" })} data-testid={`inv-adjust-${p.id}`}>Adjust…</Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <section className="mt-6 dash-surface p-5">
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1"><History className="h-3.5 w-3.5" /> Recent adjustments</div>
        {adjustments.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500">No adjustments yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100 text-sm">
            {adjustments.map((a) => (
              <li key={a.id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{a.product_name}</div>
                  <div className="text-xs text-slate-500">{a.reason} · by {a.by_user_name} · {timeAgo(a.created_at)}</div>
                </div>
                <div className={`font-mono text-sm ${a.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>{a.delta > 0 ? "+" : ""}{a.delta} → {a.stock_after}</div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Adjust stock — {editing?.product?.name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm">
                Current stock: <strong>{editing.product.stock || 0}</strong>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Change (use negative to remove)</label>
                <Input className="mt-1" type="number" value={editing.delta} onChange={(e) => setEditing({ ...editing, delta: e.target.value })} placeholder="e.g. 24 or -3" data-testid="inv-adjust-delta-input" autoFocus />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Reason</label>
                <Input className="mt-1" value={editing.reason} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} placeholder="e.g. New shipment, breakage, recount" data-testid="inv-adjust-reason-input" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={submitAdjust} disabled={busy} data-testid="inv-adjust-save-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
